// Path: ./src/app/pages/CreateBannerPage/handleSaveBanner.ts
import { useNavigate } from "react-router-dom";
import {
  CreateGfBannerDto,
  UpdateGfBannerDto,
} from "@swagger/typescript-fetch-goflyer";

import i18n from "i18next";
import { messages } from "../../../locales/messages";
import { adminApiService } from "../../../utils/api";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { useLoading } from "../../../contextApi/LoadingContext";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import {
  BANNER_CONTENT_FIELDS,
  bannerFieldLimit,
  BANNER_LOCALE_SUFFIXES,
  BANNER_MAX_TARGET_RADIUS_KM,
  bannerContentField,
} from "../../../utils/banner/bannerFields";
import {
  ctaNeedsLink,
  validateBannerLink,
} from "../../../utils/banner/bannerLinkUrl";
import { buildBannerPayload } from "../../../utils/banner/bannerPayload";

const LINK_ERROR_MESSAGE = {
  pairIncomplete: () => messages.banner_link_pair_required(),
  internalMustStartWithSlash: () => messages.banner_link_url_internal_invalid(),
  internalResolvesOffSite: () => messages.banner_link_url_offsite_invalid(),
  externalMustBeHttps: () => messages.banner_link_url_external_invalid(),
  unsafeScheme: () => messages.banner_link_url_unsafe(),
};

export const useHandleSaveBanner = () => {
  const navigate = useNavigate();
  const { showLoadingScreen } = useLoading();
  const authHeaders = useAuthHeaders();

  return async (banner?: CreateGfBannerDto, bannerId?: string) => {
    if (!banner) return;

    if (!banner.name?.trim()) {
      toastError(i18n.t(messages.banner_name_required()));
      return;
    }
    if (!banner.placement) {
      toastError(i18n.t(messages.banner_placement_required()));
      return;
    }

    const record = banner as unknown as Record<string, unknown>;
    for (const base of BANNER_CONTENT_FIELDS) {
      for (const suffix of BANNER_LOCALE_SUFFIXES) {
        const key = bannerContentField(base, suffix);
        const value = record[key];
        if (
          typeof value === "string" &&
          value.length > bannerFieldLimit(base, suffix)
        ) {
          toastError(
            `${key} ${i18n.t(messages.banner_field_too_long())} (${bannerFieldLimit(base, suffix)})`,
          );
          return;
        }
      }
    }

    const linkError = validateBannerLink(banner.linkType, banner.linkUrl);
    if (linkError) {
      toastError(i18n.t(LINK_ERROR_MESSAGE[linkError]()));
      return;
    }

    if (ctaNeedsLink(banner.cta, banner.linkUrl)) {
      toastError(i18n.t(messages.banner_cta_needs_link()));
      return;
    }

    if (!banner.desktopImageId) {
      toastError(i18n.t(messages.banner_desktop_image_required()));
      return;
    }

    if (banner.targetCenter && banner.targetRadiusKm == null) {
      toastError(i18n.t(messages.banner_radius_required_with_center()));
      return;
    }
    if (banner.targetRadiusKm != null && !banner.targetCenter) {
      toastError(i18n.t(messages.banner_center_required_with_radius()));
      return;
    }
    if (
      banner.targetRadiusKm != null &&
      banner.targetRadiusKm > BANNER_MAX_TARGET_RADIUS_KM
    ) {
      toastError(i18n.t(messages.banner_radius_too_large()));
      return;
    }

    if (banner.priority != null && banner.priority < 0) {
      toastError(i18n.t(messages.banner_priority_negative()));
      return;
    }

    const payload = buildBannerPayload(banner, { isUpdate: Boolean(bannerId) });

    showLoadingScreen(true);
    const result = bannerId
      ? await adminApiService.updateOneBaseGfBannerAdminControllerGfBanner(
          payload as UpdateGfBannerDto,
          bannerId,
          authHeaders,
        )
      : await adminApiService.createOneBaseGfBannerAdminControllerGfBanner(
          payload,
          authHeaders,
        );
    showLoadingScreen(false);

    if (result === undefined) return;

    toastSuccess(
      i18n.t(
        bannerId
          ? messages.banner_update_success()
          : messages.banner_create_success(),
      ),
    );
    navigate(-1);
  };
};
